import {
    PlaceExchangeApiFetchAdsResponse,
    PlaceExchangeAd,
    mappedPlaceExchangeAPITypes,
    placeExchangeApiFetchAdsResponseSchema,
} from './types';
import { Log } from '../../../AuxillaryService/Log';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import AdQueue from '../AdQueue';
import { PlaceExchangeError } from './types';
import { ZodError } from 'zod';
/**
 * A class that contains a few helper
 * functions to execute various http req
 * to Place Exchange's API
 */
export class PlaceExchangeApiHelper {
    /**
     * fetches ads from Place Exchange API
     * ads obj using data from Place Exchange API
     *  @see {@link https://developers.placeexchange.com/docs/api-publisher/827da5d297793-submit-ad-request Place Exchange API - To request an ad}
     */
    static async fetchAds(
        orgID: string,
        token: string,
        height: number,
        width: number,
        duration: number,
        numberOfAds: number,
        adunitName: string,
        adQueue: AdQueue<PlaceExchangeAd>
    ): Promise<void> {
        const computedMinDuration: number = Math.max(1, duration - 5);
        Log.Info(
            `Place Exchange: fetching ads ${numberOfAds} for Ad unit ${adunitName} for org ${orgID}`,
            null,
            __fileline
        );
        const options: AxiosRequestConfig = {
            method: 'POST',
            url: `https://api.placeexchange.com/v3/orgs/${orgID}/adunits/${adunitName}/adrequests`,
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            timeout: 6000,
            data: {
                name: adunitName,
                slot: {
                    h: height,
                    w: width,
                    max_duration: duration,
                    min_duration: computedMinDuration,
                },
                ad_formats: {
                    h: height,
                    w: width,
                },
            },
        };
        for (let i = 1; i <= numberOfAds; i++) {
            let response: AxiosResponse;
            try {
                response = await axios.request(options);
            } catch (error: unknown) {
                const errorMessage = this.isPlaceExchangeError(error)
                    ? error.response?.data.message
                    : String(error);
                Log.Error(
                    `Place Exchange: Failed to fetch ads for org ${orgID} on adunit ${adunitName} : ${errorMessage}`,
                    null,
                    __fileline
                );

                return;
            }
            if (response.status === 204) {
                Log.Info(
                    `Place Exchange: no new content available to play on adunit:  ${adunitName}`,
                    null,
                    __fileline
                );
                return;
            }

            const data = response.data;
            const parseResult = placeExchangeApiFetchAdsResponseSchema.safeParse(data);

            if (!parseResult.success) {
                const anError: ZodError = parseResult.error;
                Log.Error(
                    `Failed to parse place exchange ad: ${anError.message}`,
                    null,
                    __fileline
                );
                return;
            }

            const adResponseData: PlaceExchangeApiFetchAdsResponse = parseResult.data;
            //finds snapshot that matches w & h of current ad
            const snapshot =
                adResponseData.creative.snapshots.find((item) => {
                    return item.h === height && item.w === width;
                }) ?? adResponseData.creative.snapshots[0];
            const anAd: PlaceExchangeAd = {
                isFallback: false,
                adId: adResponseData.context,
                duration: adResponseData.creative.duration,
                context: adResponseData.context,
                assetUrl: snapshot.curl ? snapshot.curl : snapshot.iurl ?? '',
                filetype: mappedPlaceExchangeAPITypes[adResponseData.creative.type],
                playcount: 0,
                lastAdInLoop: false,
            };
            adQueue.enqueue(anAd);
            Log.Info(
                `pushed ad with ad id ${anAd.adId} with context ${anAd.context}`,
                null,
                __fileline
            );
        }
    }

    /**
     * sends proof of play
     * of an ad to place exchange
     * @see {@link https://developers.placeexchange.com/docs/api-publisher/761f095fa60ee-record-play Place Exchange API - Send proof of play}
     */
    static async sendProofOfPlay(
        ad: Pick<PlaceExchangeAd, 'adId' | 'context' | 'duration'>,
        orgID: string,
        adunitName: string,
        startTime: number,
        token?: string
    ): Promise<void> {
        Log.Info(
            `sending proof of play for ad ${ad.adId} with context ${ad.context}`,
            null,
            __fileline
        );
        const options: AxiosRequestConfig = {
            method: 'POST',
            url: `https://api.placeexchange.com/v3/orgs/${orgID}/adunits/${adunitName}/plays`,
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            timeout: 6000,
            data: {
                adunit: adunitName,
                start: startTime,
                duration: ad.duration,
                context: ad.context,
            },
        };
        try {
            await axios.request(options);
            Log.Info(
                `Place Exchange: successfully sent proof of play for ad ${ad.adId} with context ${ad.context}`,
                null,
                __fileline
            );
        } catch (error: unknown) {
            const errorMessage = this.isPlaceExchangeError(error)
                ? error.response?.data.message
                : String(error);
            Log.Info(
                `Place Exchange: Failed to send proof of play for ad : ${ad.adId} on adunit ${adunitName} ${errorMessage} `,
                null,
                __fileline
            );
        }
    }

    /**
     * Determines if error is place exchange error or axios error.
     */
    private static isPlaceExchangeError(err: unknown): err is PlaceExchangeError {
        return axios.isAxiosError(err) && typeof err.response?.data.message === 'string';
    }
}
